import {
  ART_CHANNEL_ID,
  GAODING_BUSINESS_ID,
  GAODING_CHANNEL_ID,
} from "@/constants/apiConfig";
import requestHB from "@/utils/request";
import { createSingleFlight } from "@/utils/singleFlight";
import { isArray } from "lodash";
import requestGd from "../utils/requestGaoding";

export function getGdUserByTel() {
  return requestHB({
    url: "/users/gd_user",
  }) as unknown as Promise<{
    gd_user: {
      nick: string;
      avatar: string;
    };
  }>;
}

export function getHuabanTempCode() {
  return requestHB({
    url: "/users/tel/code",
    method: "POST",
  }) as unknown as Promise<{
    code: string;
  }>;
}

export function checkBeforeMerge() {
  return requestGd({
    url: "/structure/hb/merge-check",
  }) as unknown as Promise<{
    result: boolean;
    temp_code: string;
    user_id: string;
    user_name: string;
    avatar: {
      url: string;
      width: number;
      height: number;
    };
    code?: string;
  }>;
}

/**
 * 取消合并中间态
 * https://yapi.gaoding.com/project/1911/interface/api/202194
 */
export function cancelMerge() {
  return requestGd({
    url: "/structure/hb/cancel-merge",
    method: "POST",
  });
}

/**
 * 绑定手机号/注册，注册稿定账号使用
 */
export function registerGd() {
  return requestHB({
    url: "/users/register_gd",
    method: "POST",
    ignoreError: true,
    ignoreMessage: true,
  }) as unknown as Promise<{
    task?: {
      task_id: number;
    };
    gd_user?: {
      nick: string;
      avatar: string;
    };
  }>;
}

/**
 * 自动创建账号绑定
 */
export function createGdAccuntAndMerge(data: {
  init_org_type: string;
  hb_user_name: string;
  hb_user_phone?: string;
}) {
  return requestGd({
    url: "/structure/hb/auto-merge",
    method: "POST",
    data,
    ignoreError: true,
  });
}
/**
 * 根据code获取用户信息及租户列表
 */
export function getGdUserByCode({ code, type }) {
  return requestGd({
    url: "/structure/hb/user-org-info",
    query: {
      code,
      type,
    },
    ignoreError: true,
  });
}
/**
 * 获取用户企业列表
 */
export function getGdUserOrgs() {
  return requestGd({
    url: "/structure/user/orgs",
    query: {
      api_info_type: "COMPANY_MEMBER_COUNT", // 成员数量
    },
  })
    .then((res) => {
      if (isArray(res)) {
        return res;
      }

      return [];
    })
    .catch(() => {
      return [];
    }) as unknown as Promise<IGdOrgInfo[]>;
}

/**
 * 确认合并接口
 * https://yapi.gaoding.com/project/1911/interface/api/183771
 */
export function mergeGdOrg(data: {
  user_id?: string;
  org_id?: string;
  is_init_org: boolean;
  init_org_type?: string;
  hb_user_name: string;
  code?: string;
  huaban_user_phone?: string;
}) {
  return requestGd({
    url: "/structure/hb/merge",
    method: "POST",
    data: data,
    ignoreError: true,
  });
}

/**
 * 查询当前用户是否进行中绑定
 * https://yapi.gaoding.com/project/1911/interface/api/183768
 */
export function checkCurrentTask() {
  return requestGd({
    url: "/structure/hb/current-user-task",
  }) as unknown as Promise<{
    task_id: number;
    status: number;
    message: string;
  }>;
}

/**
 * 查询账号绑定结果
 */
export function checkMergeResult({ taskId }: { taskId: number | string }) {
  if (taskId) {
    return requestGd({
      url: `/structure/hb/task/${taskId}`,
    });
  }
  return null;
}

/**切换租户 */
export function switchOrg(org_id: string | number) {
  return requestHB({
    url: "/me/orgs",
    method: "PUT",
    data: {
      org_id,
    },
  });
}

/**获取当前用户租户信息 */
export function getMeOrg(query = {}) {
  return requestHB({
    url: "/me/org",
    query,
  });
}

/**
 * 获取当前组织信息
 */
export function getCurrentOrg(query = {}) {
  return requestGd({
    url: "/structure/company/current",
    query,
  }) as unknown as Promise<IGdOrgInfo>;
}

/**
 * 获取当前稿定用户信息
 */
export function getCurrentUser(query = {}) {
  return requestGd({
    url: "/structure/user/current",
    query,
  }) as unknown as Promise<IGdUserInfo>;
}

/**
 * 获取当前租户稿定会员相关信息
 * https://yapi.gaoding.com/project/1911/interface/api/184709
 */
export function getOrgProduct() {
  return requestGd({
    url: "/structure/company/unify-product",
    ignoreError: true,
    ignoreMessage: true,
  }) as unknown as Promise<ICompanyUnifyProduct>;
}

/**
 * 获取dam支持文件格式（数据缓存，因为不会怎么变化）
 * https://yapi.gaoding.com/project/1536/interface/api/199696
 */
let damAcceptFileType = null;
export async function getDamAcceptFileType() {
  if (damAcceptFileType) {
    return damAcceptFileType;
  }
  const result = await requestHB({
    url: "/dam/asset/storage-format",
  });

  if (!result.err) {
    damAcceptFileType = result;
    return result;
  }
}
/**
 * 获取合并信息
 * https://yapi.gaoding.com/project/1911/interface/api/202006
 */
export async function getMergeInfo() {
  return requestGd({
    url: "/structure/hb/query-merge-info",
  });
}

/**
 * 获取下载权益次数
 */
export function getGdUserDownloadCountInfo() {
  return (
    requestGd({
      url: "/trade/rights-overview",
      query: {
        goods_type_enums: "MATERIAL_DOWNLOAD",
      },
    }) as unknown as Promise<IGdUserRightsOverview[]>
  ).then((res) => {
    if (isArray(res)) {
      return (
        res.find(
          (item) => item.goods_type_enum === "OFFICIAL_MATERIAL_DOWNLOAD"
        ) || res.find((item) => item.goods_type_enum === "MATERIAL_DOWNLOAD")
      );
    }
    return {
      left: 0,
      quantity: 0,
      used: 0,
      total: 0,
    };
  });
}

export function getGdUserDownloadRight() {
  return requestGd({
    url: "/trade/rights-overview",
    query: {
      goods_type_enums: "MATERIAL_DOWNLOAD",
    },
  }).then((res) => {
    if (isArray(res)) {
      return res;
    }
    return [];
  }) as unknown as Promise<IGdUserRightsOverview[]>;
}

/**
 * 获取租户优惠券
 * https://yapi.gaoding.com/project/1919/interface/api/171429
 */
export function getGdCoupons(query = {}) {
  return requestGd({
    url: "/trade/org-coupons",
    query,
  }).then((res) => {
    res = res as unknown as {
      data: ICouponItem[];
      pagination: {
        total: number;
        page: number;
        page_size: number;
      };
    };
    return {
      list: res.data,
      ...res.pagination,
    } as {
      list: ICouponItem[];
      total: number;
      size: number;
      number: number;
    };
  });
}
/**
 * https://yapi.gaoding.com/project/1919/interface/api/194573
 * 获取优惠券数量
 */
export function getGdCouponNumber() {
  return requestGd({
    url: "/trade/org-coupon-status",
  }) as Promise<{
    count: number;
    expired_count: number;
    un_used_count: number;
    used_count: number;
  }>;
}

export const getGdVipInfo = createSingleFlight(function getGdVipInfo(
  query: {
    //嵌入数据，支持 pending_vips, vip_subscription
    embed?: string;
  } = {}
) {
  return requestGd({
    url: "/trade/v2/org-vip",
    query,
  }).catch(() => {
    return {
      vip: [],
    };
  }) as Promise<IGdOrgVip>;
});

export function getGdVipUnpaid() {
  return (
    requestGd({
      url: "/trade/orders/vip-unpaid",
      query: {
        status: 0,
      },
      ignoreMessage: true,
      ignoreError: true,
    }) as Promise<IGDUnpayOrderInfo[]>
  )
    .then((res) => {
      if ((res as unknown as { err: number }).err) {
        return [];
      }
      return res;
    })
    .catch(() => {
      return [];
    });
}

export enum UpgradeType {
  /**
   * 素材会员=个人版会员（id=189、155、191）且 稿定模板会员=团队版、团队免费版 => 提示用户将素材会员升级至 团队版会员（id=156）
   */
  MATERIAL_PERSONAL_TO_ORG = "MATERIAL_PERSONAL_TO_ORG",
  /**
   * 素材会员=团队版（id=156、192）且 稿定模板会员=个人免费版 => 提示用户将租户类型升级至团队免费版
   */
  FREE_PERSONAL_TO_ORG_FREE = "FREE_PERSONAL_TO_ORG_FREE",
  /**
   * 素材会员=团队版（id=156、192）且 稿定模板会员=基础版 => 提示用户将模板会员升级至套版团队版会员
   */
  DESIGN_PERSONAL_TO_ORG = "DESIGN_PERSONAL_TO_ORG",

  /**无需处理 */
  IGNORE = "IGNORE",
}

/**检查会员版本是否冲突 */
export function checkVipIsCorrect() {
  return requestGd({
    url: "/trade/material-correction-check",
  }) as Promise<{ type: UpgradeType }>;
}

/**纠正会员版本 */
export function materialCorrection() {
  return requestGd({
    url: "/trade/material-correction",
    method: "POST",
  }) as Promise<{
    id: number;
    status: "PROCESSING" | "SUCCESS" | "FAIL";
  }>;
}

export function getMaterialCorrectionTask(id) {
  return requestGd({
    url: `/trade/material-correction/${id}`,
  }) as Promise<{
    id: number;
    status: "PROCESSING" | "SUCCESS" | "FAIL";
  }>;
}

export function integrationAutomerge() {
  return requestGd({
    url: "/structure/integration/auto-merge",
    method: "POST",
  }) as Promise<{
    /**SUCCESS,HAS_MERGE_TASK,HAS_SAME_ACCOUNT,HAS_HB_PENDING_PAY_ORDER,HAS_GD_PENDING_PAY_ORDER,HAS_TEAM_ORG,HAS_NO_SAME_ACCOUNT,HAS_PLUS_MATERIAL_VIP,HAS_OLD_ACCOUNT */
    result_type: string;
    temp_code?: string;
    is_subscribe_order?: boolean;
    [k: string]: any;
  }>;
}
/**
 * 获取稿定的投放数据
 * @param {string} key - 请求参数 key
 * @returns {Promise<any>}
 */
export function getGdDeliveryDetail(key: string) {
  return requestGd({
    url: `/v3/oc/v2/delivery-pits/${key}/modules`,
    headers: {
      "x-business-id": GAODING_BUSINESS_ID,
      "x-channel-id": GAODING_CHANNEL_ID,
    },
  });
}

interface IAddImageAnalysisTaskResponse {
  id: string;
}

interface IAddImageAnalysisTaskQuery {
  url: string;
}

/**
 * 添加图片分析任务
 */
export function addImageAnalysisTask(query: IAddImageAnalysisTaskQuery) {
  return requestGd({
    url: "/gaoding-art/v1/image-analysis-tasks/add",
    method: "POST",
    data: query,
    headers: {
      "X-Channel-Id": ART_CHANNEL_ID,
    },
  }) as Promise<IAddImageAnalysisTaskResponse>;
}

export function createTemporaryStorage(key: string, value: string) {
  return requestGd({
    url: `/gaoding-art/v1/temporary-storage/${key}`,
    method: "POST",
    data: {
      value: value,
    },
  }) as Promise<{ key: string; value: string }>;
}
interface IArtSearchContentQuery {
  q?: string;
  page_num?: number;
  page_size?: number;
  /** 0: 模板，1: 作品*/
  ugc_publish_type?: number;
  sort?: string;
  position?: string;
  filter_id?: string;
  pri_classify?: number;
  file_id?: number;
}

interface IArtSearchContentResponse {
  data: {
    id: string;
    preview: {
      url: string;
      width: number;
      height: number;
      hex: string;
    };
  }[];
  pagination: {
    num: number;
    size: number;
    total: number;
  };
}

/**
 * https://yapi.gaoding.com/project/1566/interface/api/148812
 * @param query
 * @returns
 */
export function getArtSearchContent(query: IArtSearchContentQuery) {
  return requestGd({
    url: "/v3/cp/search-contents/v2/simple",
    method: "POST",
    data: query,
    headers: {
      "X-Channel-Id": ART_CHANNEL_ID,
    },
  }).catch(() => {
    return {
      data: [],
    };
  }) as Promise<IArtSearchContentResponse>;
}

/**
 * https://yapi.gaoding.com/project/1566/interface/api/210170
 * https://yapi.gaoding.com/project/1566/interface/api/148812
 * @param query
 * @returns
 */
export async function getArtImageSearchContent(query: IArtSearchContentQuery) {
  return requestGd({
    url: "/gaoding-art/v1/contents/image-search",
    method: "POST",
    data: query,
    headers: {
      "X-Channel-Id": ART_CHANNEL_ID,
    },
  }).catch(() => {
    return {
      data: [],
    };
  }) as Promise<IArtSearchContentResponse>;
}
